import React, { useRef, useEffect, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Hands, HAND_CONNECTIONS } from '@mediapipe/hands';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import { Volume2, VolumeX, Download, Trash2, CloudUpload, Wifi } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// ─────────────────────────────────────────────────────────────
// Gesture definitions  [thumb, index, middle, ring, pinky]
//   1 = extended  |  0 = curled
// ─────────────────────────────────────────────────────────────
const GESTURES = [
  { name: 'Hello',     f: [1, 1, 1, 1, 1] },
  { name: 'B',         f: [0, 1, 1, 1, 1] },
  { name: 'W',         f: [0, 1, 1, 1, 0] },
  { name: 'Water',     f: [0, 1, 1, 1, 0] },
  { name: 'V',         f: [0, 1, 1, 0, 0] },
  { name: 'No',        f: [0, 1, 1, 0, 0] },
  { name: 'L',         f: [1, 1, 0, 0, 0] },
  { name: 'I',         f: [0, 0, 0, 0, 1] },
  { name: 'Y',         f: [1, 0, 0, 0, 1] },
  { name: 'Love',      f: [1, 1, 0, 0, 1] },
  { name: 'Yes',       f: [1, 0, 0, 0, 0] },
  { name: 'Help',      f: [0, 0, 0, 0, 0] },
];

const cosineSim = (a, b) => {
  const dot  = a.reduce((s, v, i) => s + v * b[i], 0);
  const magA = Math.sqrt(a.reduce((s, v) => s + v * v, 0));
  const magB = Math.sqrt(b.reduce((s, v) => s + v * v, 0));
  return (magA && magB) ? dot / (magA * magB) : 0;
};

const getFingerStates = (lm) => {
  const tips = [4, 8, 12, 16, 20];
  const pips = [3, 6, 10, 14, 18];

  // Thumb: horizontal OR vertical extension
  const horizExt = Math.abs(lm[4].x - lm[0].x) > Math.abs(lm[2].x - lm[0].x) * 1.1;
  const vertExt  = (lm[0].y - lm[4].y) > 0.08;
  const states   = [horizExt || vertExt ? 1 : 0];

  for (let i = 1; i < 5; i++) {
    states.push(lm[tips[i]].y < lm[pips[i]].y ? 1 : 0);
  }
  return states;
};

const classifyGesture = (lm) => {
  const states = getFingerStates(lm);
  let best = null, bestScore = 0;
  for (const g of GESTURES) {
    const s = cosineSim(states, g.f);
    if (s > bestScore) { bestScore = s; best = g.name; }
  }
  const confidence = Math.round(bestScore * 100);
  return { sign: confidence >= 72 ? best : 'Unknown', confidence };
};

// ── Module-level smoothing buffer ─────────────────────────────
const sigBuf = [];
const smoothSign = (s) => {
  sigBuf.push(s);
  if (sigBuf.length > 5) sigBuf.shift();
  const freq = {};
  sigBuf.forEach(v => { freq[v] = (freq[v] || 0) + 1; });
  return Object.keys(freq).reduce((a, b) => freq[a] > freq[b] ? a : b);
};

// ─────────────────────────────────────────────────────────────
const WebcamFeed = () => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const handsRef  = useRef(null);
  const rafRef    = useRef(null);

  // ── Refs for mutable values used inside MediaPipe callback ──
  // (Using refs avoids recreating the callback and restarting MediaPipe)
  const lastSignRef     = useRef('');
  const lastSignTimeRef = useRef(0);
  const soundOnRef      = useRef(true);

  // ── React state (UI only) ───────────────────────────────────
  const [currentSign, setCurrentSign] = useState('');
  const [sentence,    setSentence]    = useState('');
  const [history,     setHistory]     = useState([]);
  const [soundOn,     setSoundOn]     = useState(true);
  const [isRunning,   setIsRunning]   = useState(true);
  const [confidence,  setConfidence]  = useState(0);
  const [handsFound,  setHandsFound]  = useState(0);
  const [modelReady,  setModelReady]  = useState(false);
  const [isSaving,    setIsSaving]    = useState(false);
  const [proMode,     setProMode]     = useState(false);
  const [rawStates,   setRawStates]   = useState([0,0,0,0,0]);
  const proModeRef    = useRef(false);

  useEffect(() => {
    const isPro = localStorage.getItem('proMode') === 'true';
    setProMode(isPro);
    proModeRef.current = isPro;
  }, []);

  // Keep soundOnRef in sync with state
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  const speak = (word) => {
    if (!soundOnRef.current || !word || word === 'Unknown') return;
    const u = new SpeechSynthesisUtterance(word);
    u.rate = 0.85;
    speechSynthesis.cancel();
    speechSynthesis.speak(u);
  };

  // ── The stable MediaPipe callback (created ONCE, never changes)
  const onResultsStable = useCallback((results) => {
    // Draw landmarks
    const canvas = canvasRef.current;
    const video  = webcamRef.current?.video;
    if (canvas && video) {
      canvas.width  = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (results.multiHandLandmarks) {
        for (const lm of results.multiHandLandmarks) {
          // Mirror x to match the mirrored webcam feed
          const m = lm.map(p => ({ ...p, x: 1 - p.x }));
          drawConnectors(ctx, m, HAND_CONNECTIONS, { color: 'rgba(16,185,129,0.8)', lineWidth: 2 });
          drawLandmarks(ctx, m, { color: '#10b981', lineWidth: 1, radius: 4 });
        }
      }
    }

    const count = results.multiHandLandmarks?.length || 0;
    setHandsFound(count);

    if (count === 0) {
      setConfidence(0);
      if (proModeRef.current) setRawStates([0,0,0,0,0]);
      return;
    }

    const states = getFingerStates(results.multiHandLandmarks[0]);
    if (proModeRef.current) setRawStates(states);

    const { sign, confidence: conf } = classifyGesture(results.multiHandLandmarks[0]);
    const smoothed = smoothSign(sign);
    setConfidence(conf);

    if (!smoothed || smoothed === 'Unknown') return;

    const now = Date.now();
    const COOLDOWN = 2500; // ms before same sign fires again
    const isDiff    = smoothed !== lastSignRef.current;
    const isCooled  = (now - lastSignTimeRef.current) > COOLDOWN;

    if (isDiff || isCooled) {
      lastSignRef.current     = smoothed;
      lastSignTimeRef.current = now;

      setCurrentSign(smoothed);
      setSentence(prev => (prev ? prev + ' ' : '') + smoothed);
      setHistory(prev => [
        { word: smoothed, conf, time: new Date().toLocaleTimeString() },
        ...prev.slice(0, 9)
      ]);
      speak(smoothed);
    }
  }, []); // eslint-disable-next-line react-hooks/exhaustive-deps

  // ── Init MediaPipe ONCE ───────────────────────────────────
  useEffect(() => {
    const hands = new Hands({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4/${file}`,
    });

    hands.setOptions({
      maxNumHands:            1,
      modelComplexity:        1,
      minDetectionConfidence: 0.7,
      minTrackingConfidence:  0.6,
    });

    hands.onResults(onResultsStable);
    handsRef.current = hands;

    hands.initialize().then(() => {
      console.log('MediaPipe Hands ready');
      setModelReady(true);
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      hands.close();
    };
  }, [onResultsStable]); // onResultsStable never changes → effect runs once

  // ── Detection loop ─────────────────────────────────────────
  useEffect(() => {
    if (!modelReady) return;

    let lastTime = 0;
    const FPS = 12;

    const loop = async (ts) => {
      if (!isRunning) { rafRef.current = requestAnimationFrame(loop); return; }
      if (ts - lastTime > 1000 / FPS) {
        const video = webcamRef.current?.video;
        if (video && video.readyState === 4 && handsRef.current) {
          try { await handsRef.current.send({ image: video }); } catch (_) {}
        }
        lastTime = ts;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [modelReady, isRunning]);

  // ── Actions ────────────────────────────────────────────────
  const clearAll = () => {
    setSentence(''); setCurrentSign(''); setConfidence(0);
    lastSignRef.current = ''; sigBuf.length = 0;
  };

  const saveToCloud = async () => {
    if (!sentence.trim()) return;
    setIsSaving(true);
    try {
      await addDoc(collection(db, 'translations'), {
        text: sentence.trim(), timestamp: serverTimestamp(),
        signsCount: sentence.trim().split(' ').length,
      });
      alert('Saved to Firebase ✓');
    } catch (e) { alert('Error: ' + e.message); }
    setIsSaving(false);
  };

  const downloadTxt = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([sentence], { type: 'text/plain' }));
    a.download = 'translation.txt'; a.click();
  };

  const confColor = confidence > 72 ? '#10b981' : confidence > 45 ? '#f59e0b' : '#ef4444';

  return (
    <div className="translate-container">

      {/* ── Camera ── */}
      <div className="camera-section">
        <div className="glass-panel" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:10, height:10, borderRadius:'50%', background: modelReady ? '#10b981' : '#f59e0b', boxShadow: modelReady ? '0 0 0 3px rgba(16,185,129,0.3)' : 'none' }} />
            <h3 style={{ margin:0 }}>{modelReady ? 'MediaPipe Ready' : 'Loading AI Model…'}</h3>
          </div>
          <button className="btn" onClick={() => setIsRunning(p => !p)}
            style={{ background: isRunning ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: isRunning ? '#ef4444' : '#10b981' }}>
            {isRunning ? 'Pause' : 'Resume'}
          </button>
        </div>

        <div className="video-wrapper">
          <Webcam ref={webcamRef} mirrored audio={false}
            style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          <canvas ref={canvasRef}
            style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', pointerEvents:'none' }} />

          {/* Live overlay */}
          <div style={{ position:'absolute', bottom:20, left:20, display:'flex', gap:16, background:'rgba(0,0,0,0.65)', padding:'12px 20px', borderRadius:12, backdropFilter:'blur(8px)' }}>
            {[
              { label: 'SIGN',       value: currentSign || '—',            color: 'white' },
              { label: 'CONFIDENCE', value: confidence > 0 ? `${confidence}%` : '—', color: confColor },
              { label: 'HANDS',      value: handsFound,                    color: handsFound > 0 ? '#10b981' : 'rgba(255,255,255,0.4)' },
            ].map((item, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div style={{ width:1, background:'rgba(255,255,255,0.15)' }} />}
                <div>
                  <p style={{ margin:0, color:'rgba(255,255,255,0.6)', fontSize:11 }}>{item.label}</p>
                  <h2 style={{ margin:0, color:item.color, fontSize:22 }}>{item.value}</h2>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Pro Mode HUD */}
          {proMode && (
            <div style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(0,0,0,0.8)', padding: '12px 16px', borderRadius: '8px', color: '#10b981', fontFamily: 'monospace', fontSize: '12px', border: '1px solid #10b981', boxShadow: '0 0 10px rgba(16,185,129,0.2)' }}>
              <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#f59e0b' }}>PRO ANALYTICS HUD</div>
              <div>THUMB:  {rawStates[0] ? 'EXT' : 'FLX'}</div>
              <div>INDEX:  {rawStates[1] ? 'EXT' : 'FLX'}</div>
              <div>MIDDLE: {rawStates[2] ? 'EXT' : 'FLX'}</div>
              <div>RING:   {rawStates[3] ? 'EXT' : 'FLX'}</div>
              <div>PINKY:  {rawStates[4] ? 'EXT' : 'FLX'}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Output Panel ── */}
      <div className="glass-panel" style={{ display:'flex', flexDirection:'column', gap:12 }}>
        <h3 style={{ margin:0 }}>Translation Output</h3>

        <textarea className="sentence-display" value={sentence}
          placeholder="Start signing — words will appear here…" readOnly
          style={{ flex:1, minHeight:140 }} />

        <div className="controls-row">
          <button className="btn btn-primary" onClick={saveToCloud} disabled={!sentence || isSaving}
            style={{ flex:1, justifyContent:'center', opacity: (!sentence || isSaving) ? 0.5 : 1 }}>
            <CloudUpload size={16}/> {isSaving ? 'Saving…' : 'Save to Cloud'}
          </button>
          <button className="btn" onClick={clearAll}
            style={{ flex:1, justifyContent:'center', background:'rgba(0,0,0,0.05)', color:'var(--text-main)' }}>
            <Trash2 size={16}/> Clear
          </button>
        </div>

        <div className="controls-row">
          <button className="btn" onClick={() => setSoundOn(p => !p)}
            style={{ flex:1, justifyContent:'center', background: soundOn ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: soundOn ? '#10b981' : '#ef4444' }}>
            {soundOn ? <Volume2 size={16}/> : <VolumeX size={16}/>} TTS {soundOn ? 'On' : 'Off'}
          </button>
          <button className="btn" onClick={downloadTxt}
            style={{ flex:1, justifyContent:'center', background:'rgba(0,0,0,0.05)', color:'var(--text-main)' }}>
            <Download size={16}/> Export
          </button>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'var(--text-muted)', padding:'8px 0' }}>
          <Wifi size={14} color="#10b981"/>
          <span>On-device AI — no server needed for detection</span>
        </div>

        <h4 style={{ margin:'4px 0 6px', color:'var(--text-muted)', fontSize:13, fontWeight:600 }}>RECENT SIGNS</h4>
        <div className="history-list">
          {history.length > 0 ? history.map((item, i) => (
            <div key={i} className="history-item">
              <span style={{ fontWeight:600 }}>{item.word}</span>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <span style={{ fontSize:11, color: item.conf > 72 ? '#10b981' : '#f59e0b', fontWeight:700 }}>{item.conf}%</span>
                <span style={{ fontSize:11, color:'var(--text-muted)' }}>{item.time}</span>
              </div>
            </div>
          )) : (
            <p style={{ textAlign:'center', color:'var(--text-muted)', fontSize:13, margin:'16px 0' }}>No signs yet — start signing!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebcamFeed;
